import { Link, createFileRoute, notFound, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowUpRight, Heart, UtensilsCrossed, Star as StarLucide, CalendarDays, Map as MapIcon,
  Bus, ParkingCircle, Info, TrainFront,
  Sun, Leaf, Snowflake, Sprout, ChevronRight, MapPin,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { CITIES, type CityId } from "@/data/cities";
import { useCity } from "@/context/CityContext";
import { useListedPlaces, usePublicPlacesLoading } from "@/hooks/use-public-places";
import type { usePublicPlaces } from "@/hooks/use-public-places";

import { useFavorites } from "@/hooks/useFavorites";
import { SmartImage } from "@/components/ui/SmartImage";
import { useLanguage } from "@/i18n/LanguageContext";
import heroHelsinki from "@/assets/hero-helsinki.webp";
import { buildHreflangLinks, buildLocaleMeta } from "@/lib/i18n-paths";
import { getCitySeoCopy } from "@/lib/city-seo";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { lazy, Suspense } from "react";

const MiniPlatformMap = lazy(() => import("@/components/MiniPlatformMap"));
import MarqueeRail from "@/components/MarqueeRail";
import { useHideGlobalMapFab } from "@/lib/map-fab-visibility";




type PublicCityFull = {
  slug: string;
  name: string;
  tagline: string | null;
  region: string | null;
  population: string | null;
  founded: number | null;
  image_url: string | null;
  accent: string | null;
  website_url: string | null;
  instagram_url: string | null;
  facebook_url: string | null;
};

// Reserved single-segment routes — never treat these as city slugs.
const RESERVED = new Set([
  "cities", "eat", "do", "happening", "contact", "help", "ai", "search",
  "login", "register", "onboarding", "profile", "saved", "place",
  "admin", "api", "_authenticated", "sitemap.xml",
]);

import { CityLandingPage } from "@/components/city/CityLandingPage";
export const Route = createFileRoute("/$citySlug")({
  loader: ({ params }) => {
    if (RESERVED.has(params.citySlug)) throw notFound();
    return { citySlug: params.citySlug };
  },
  head: ({ params }) => {
    const slug = params.citySlug;
    const name = slug.charAt(0).toUpperCase() + slug.slice(1).toLowerCase();
    const copy = getCitySeoCopy(slug, name);
    const title = copy.title;
    const desc = copy.description;

    const url = `https://whatfinland.com/${slug}`;
    const breadcrumbs = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "what Finland", item: "https://whatfinland.com/" },
        { "@type": "ListItem", position: 2, name: "Cities", item: "https://whatfinland.com/cities" },
        { "@type": "ListItem", position: 3, name, item: url },
      ],
    };
    const cityLd = {
      "@context": "https://schema.org",
      "@type": ["City", "TouristDestination"],
      "@id": `${url}#city`,
      name,
      url,
      containedInPlace: { "@type": "Country", name: "Finland" },
      description: desc,
      touristType: ["leisure", "city break", "food and drink", "culture", "sauna"],
    };
    const faq = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: `What is the best way to spend a weekend in ${name}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `what Finland's editors recommend mixing a curated restaurant from /eat, an experience or sauna from /do, and one live event from /happening. The ${name} hub at ${url} lists all three in one place, updated weekly.`,
          },
        },
        {
          "@type": "Question",
          name: `Where should I eat in ${name}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `Editor-picked restaurants, cafés, bakeries and bars in ${name} are listed on the Eat tab of the ${name} city hub at ${url}, refreshed by local writers.`,
          },
        },
        {
          "@type": "Question",
          name: `What are the top things to do in ${name}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `Saunas, archipelago trips, design walks, museums and bookable experiences in ${name} are curated on the Do tab of ${url}.`,
          },
        },
        {
          "@type": "Question",
          name: `What events are happening in ${name}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `Concerts, exhibitions, festivals and cultural events in ${name} are listed on the Happening tab of ${url}, with dates, venues and direct ticket links where available.`,
          },
        },
        {
          "@type": "Question",
          name: `Is ${name} worth visiting?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `${name} is one of five Finnish cities what Finland covers in depth (alongside Helsinki, Turku, Tampere, Hanko and Porvoo). The editors publish a current shortlist of restaurants, things to do and live events at ${url}.`,
          },
        },
      ],
    };
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { name: "keywords", content: copy.keywords },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        ...buildLocaleMeta("en"),
        { property: "og:type", content: "website" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
      ],
      links: [{ rel: "canonical", href: url }, ...buildHreflangLinks(`/${slug}`)],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(breadcrumbs) },
        { type: "application/ld+json", children: JSON.stringify(cityLd) },
        { type: "application/ld+json", children: JSON.stringify(faq) },
      ],
    };
  },

  component: CityLandingPage,
});
